import type { RaceClientMsg, RaceServerMsg, RaceState } from '../../shared/types';

export type RaceEvent =
  | { t: 'open' }
  | { t: 'welcome'; you: string; state: RaceState }
  | { t: 'state'; state: RaceState }
  | { t: 'go'; startsAt: number }
  | { t: 'error'; message: string }
  | { t: 'closed' };

/**
 * Thin WebSocket client for a race room. Estimates the clock offset to the
 * server so both phones can start the countdown on the same wall clock even if
 * their device clocks disagree.
 */
export class RaceClient {
  private ws: WebSocket | null = null;
  private pingTimer: number | null = null;
  private pingSentAt = new Map<number, number>();
  private pingSeq = 0;
  private offsets: number[] = [];
  private lastSend = 0;

  /** Server time minus local time, in ms. */
  clockOffset = 0;
  state: RaceState | null = null;
  you = '';

  constructor(
    readonly code: string,
    readonly name: string,
    private readonly onEvent: (e: RaceEvent) => void,
  ) {}

  connect(): void {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws/race/${encodeURIComponent(this.code)}?name=${encodeURIComponent(
      this.name,
    )}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.onEvent({ t: 'open' });
      this.send({ t: 'join', name: this.name });
      this.ping();
      this.pingTimer = window.setInterval(() => this.ping(), 3000);
    };

    ws.onmessage = (ev) => {
      let msg: RaceServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as RaceServerMsg;
      } catch {
        return;
      }
      switch (msg.t) {
        case 'welcome':
          this.you = msg.you;
          this.state = msg.state;
          this.onEvent(msg);
          break;
        case 'state':
          this.state = msg.state;
          this.onEvent(msg);
          break;
        case 'go':
          this.onEvent(msg);
          break;
        case 'pong': {
          const sent = this.pingSentAt.get(msg.n);
          if (sent !== undefined) {
            this.pingSentAt.delete(msg.n);
            const now = performance.timeOrigin + performance.now();
            const rtt = now - sent;
            this.offsets.push(msg.now + rtt / 2 - now);
            if (this.offsets.length > 7) this.offsets.shift();
            const sorted = [...this.offsets].sort((a, b) => a - b);
            this.clockOffset = sorted[Math.floor(sorted.length / 2)]!;
          }
          break;
        }
        case 'error':
          this.onEvent(msg);
          break;
      }
    };

    ws.onclose = () => {
      this.stopPing();
      this.onEvent({ t: 'closed' });
    };
    ws.onerror = () => {
      this.onEvent({ t: 'error', message: 'Connection failed' });
    };
  }

  private ping(): void {
    const n = ++this.pingSeq;
    this.pingSentAt.set(n, performance.timeOrigin + performance.now());
    this.send({ t: 'ping', n });
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  send(msg: RaceClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  ready(v: boolean): void {
    this.send({ t: 'ready', ready: v });
  }

  /** Position updates, throttled — the DO rebroadcasts at 10 Hz anyway. */
  pushState(s: { z: number; x: number; y: number; speed: number; score: number; crashes: number }): void {
    const now = performance.now();
    if (now - this.lastSend < 80) return;
    this.lastSend = now;
    this.send({ t: 'state', ...s });
  }

  finish(ms: number, score: number): void {
    this.send({ t: 'finish', ms, score });
  }

  /** Server wall-clock now, corrected for the measured offset. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  close(): void {
    this.stopPing();
    this.ws?.close();
    this.ws = null;
  }
}
